// Join integration - csv export of line items, markups, and alternates
// This code is for exporting data from the Join application in a specific format

import { splitLineItemsByDimension } from "../../excel/utils/lineItems";
import { getEstimateReportData } from "../../excel/workbook";
import { ValidationError } from "../../errors";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { getProcessedEstimate } from "../../excel/datasources/estimate";
import { transformUseGroups } from "../../excel/datasources/useGroups";
import { reportLineItemsWithAllocatedMarkups } from "../../excel/utils/markups";

export async function createJoinExport(): Promise<[string[], string]> {
  const reportData = await getEstimateReportData();
  const estimateId = reportData[0]?.estimate_id as string;
  if (!estimateId) throw new ValidationError("Load an estimate before exporting.");
  const processed = await getProcessedEstimate(estimateId);
  const tabulatedEstimate = processed?.baseBidItems ?? [];
  const sortFields = Object.values(processed?.usedSortFields ?? {}).reduce((acc: Record<string, string>, sf) => {
    acc[sf.name] = sf.key;
    return acc;
  }, {});
  let rawLineItems = tabulatedEstimate.filter((row) => row.type === "line_item");
  const markups = tabulatedEstimate.filter((row) => row.type === "markup");
  const master: string[] = [];

  // Get use groups for splitting
  const allUseGroups: ReportUseGroup[] = transformUseGroups(
    processed?.estimateData?.estimate?.stats?.useGroups ?? [],
    processed?.estimateData?.floor_groups ?? []
  );

  // Dedupe by name before splitting. splitLineItemsByDimension matches a line item
  // to groups by display name and emits one row per match, so two use-group entries
  // resolving to the same name (e.g. the same use group reused across floor groups)
  // each match and produce identical duplicate rows. The per-name `${name}_$`
  // allocation column is itself keyed by name (last-write-wins upstream), so the
  // collapsed group carries the same allocation value regardless.
  const seenUseGroupNames = new Set<string>();
  const useGroups: ReportUseGroup[] = allUseGroups.filter((ug) => {
    if (seenUseGroupNames.has(ug.name)) return false;
    seenUseGroupNames.add(ug.name);
    return true;
  });

  // Apply markups before splitting (consistent with estimate.ts and sov.ts)
  rawLineItems = reportLineItemsWithAllocatedMarkups(rawLineItems);

  // Split line items by use_group using shared function
  const lineItems = splitLineItemsByDimension(rawLineItems, useGroups, "use_group", "_$", (group) => group.name);

  // Unique use groups for numbering/indexing
  const uniqueUseGroupsSet = new Set(lineItems.map((item) => item.use_group));
  const uniqueUseGroups = useGroups
    .filter((ug) => uniqueUseGroupsSet.has(ug.name))
    .sort((a, b) => a.order - b.order)
    .map((ug) => ug.name);

  // Check if any line items have alternate data
  const hasAlternateData = lineItems.some((item) => item.alternateId || item.alternate_code || item.alternate_desc);

  // Transform line items
  const transformedLineItems = lineItems
    .filter((item) => typeof item.total !== "string") //filter out total cost text overrides
    .map((item) => {
      const tempFields: Record<string, any> = {};

      // Store all keys/value pairs first
      Object.keys(item).forEach((key) => {
        if (key.includes("_code")) {
          if (key.slice(0, 2) !== "mf" && key.slice(0, 2) !== "uf" && key.replace("_code", "") !== "alternate") {
            tempFields[`${sortFields[key.replace("_code", "")]} - Number`] = item[key];
          } else {
            if (key.replace("_code", "") === "alternate" && hasAlternateData) {
              // print alt id if available. This allows the join export to be created with said id
              tempFields["alternate - Number"] = item.alternateId;
            } else if (key.replace("_code", "") !== "alternate") {
              tempFields[key.replace("_code", " - Number")] = item[key];
            }
          }
        } else if (key.includes("_desc")) {
          if (key.slice(0, 2) !== "mf" && key.slice(0, 2) !== "uf" && key.replace("_desc", "") !== "alternate") {
            tempFields[`${sortFields[key.replace("_desc", "")]} - Name`] = item[key];
          } else {
            if (key.replace("_desc", "") !== "alternate" || hasAlternateData) {
              tempFields[key.replace("_desc", " - Name")] = item[key];
            }
          }
        } else if (key === "name") {
          tempFields["Description"] = item[key];
        } else if (key === "quantity") {
          tempFields["Quantity"] = item[key];
        } else if (key === "uom") {
          tempFields["Unit"] = item[key];
        } else if (key === "product") {
          tempFields["product - Name"] = "";
          tempFields["product - Number"] = item[key];
        } else if (key === "use_group") {
          tempFields["use_group - Name"] = item[key];
          tempFields["use_group - Number"] =
            item[key]?.toString().trim() !== "" ? uniqueUseGroups.indexOf(item[key]) + 1 : "";
        }
      });

      // Unit prices - markups already applied by reportLineItemsWithAllocatedMarkups
      tempFields["Labor Unit Price"] = Number(item.labor_uc) || 0;
      tempFields["Equipment Unit Price"] = Number(item.equip_uc) || 0;
      tempFields["Material Unit Price"] = Number(item.material_uc) || 0;
      tempFields["Subcontractor Unit Price"] = Number(item.sub_uc) || 0;
      tempFields["Other Unit Price"] = Number(item.other_uc) || 0;
      tempFields["Cost"] = Number(item.quantity * (item.total_uc || 0)) || 0;

      // Reorder fields: number-name pairs first, then others
      const groupedKeys: Record<string, any> = {};
      const otherFields: [string, any][] = [];

      for (const key in tempFields) {
        if (key.endsWith(" - Number") || key.endsWith(" - Name")) {
          const base = key.replace(/ - (Number|Name)$/, "");
          groupedKeys[base] = groupedKeys[base] || {};
          if (key.endsWith(" - Number")) groupedKeys[base].number = key;
          else groupedKeys[base].name = key;
        } else {
          otherFields.push([key, tempFields[key]]);
        }
      }

      const orderedItem: Record<string, any> = {};

      // First: number-name pairs
      const sortedGroupBases = Object.keys(groupedKeys).sort();
      sortedGroupBases.forEach((base) => {
        const group = groupedKeys[base];
        if (group.number) orderedItem[group.number] = tempFields[group.number];
        if (group.name) orderedItem[group.name] = tempFields[group.name];
      });

      // Then: all other fields
      otherFields.forEach(([key, value]) => {
        orderedItem[key] = value;
      });

      // Move "Cost" to end
      if ("Cost" in orderedItem) {
        const costValue = orderedItem["Cost"];
        delete orderedItem["Cost"];
        orderedItem["Cost"] = costValue;
      }

      return orderedItem;
    });

  // Transform markups
  const transformedMarkups = markups
    .filter((markup) => markup.total !== 0)
    .map((markup) => {
      if (markup.quantity !== 0) {
        const newMarkup: Record<string, any> = {};
        newMarkup["Description"] = markup["name"];
        newMarkup["Type"] = "PERCENT";
        newMarkup["Value"] = markup["quantity"];
        newMarkup["Allocate"] = "FALSE";
        newMarkup["Total"] = markup["total"];
        return newMarkup;
      } else {
        const newMarkup: Record<string, any> = {};
        newMarkup["Description"] = markup["name"];
        newMarkup["Type"] = "FIXED";
        newMarkup["Value"] = markup["total"];
        newMarkup["Allocate"] = "FALSE";
        newMarkup["Total"] = "";
        return newMarkup;
      }
    });

  // Assemble CSV: Lines
  master.push("Lines:");
  if (transformedLineItems.length > 0) {
    // Rows do not all carry the same keys — a sort-code field (e.g. `mf3 - Number`)
    // only exists on a row whose line item has that sort assignment. Deriving the
    // header from the first row and writing each row's values positionally would
    // shift every column left for any row missing an earlier field. Instead build one
    // stable column set from the union of all rows, then emit each row against it
    // (absent keys render blank). Column order mirrors the per-row intent: `- Number`/
    // `- Name` pairs first (sorted by base, Number before Name), then remaining fields
    // in first-seen order, with `Cost` last.
    const columns = buildLineColumns(transformedLineItems);
    master.push(columns.map((key) => `"${csvEscapeField(key)}"`).join(","));
    for (const row of transformedLineItems) {
      master.push(columns.map((key) => `"${csvEscapeField(row[key])}"`).join(","));
    }
  }

  // Assemble CSV: Markups
  master.push("Markups:");
  if (transformedMarkups.length > 0) {
    master.push(
      Object.keys(transformedMarkups[0])
        .map((key) => `"${csvEscapeField(key)}"`)
        .join(",")
    );
    for (const row of transformedMarkups) {
      const markup = Object.values(row)
        .map((value) => `"${csvEscapeField(value)}"`)
        .join(",");
      master.push(markup);
    }
  }

  return [master, "csv"];
}
// Build the stable, ordered column set for the Lines section from the union of every
// row's keys. Rows can omit fields (a sort-code column only exists when the line item
// has that assignment), so the header must cover all of them or values shift out of
// alignment. Ordering mirrors the per-row layout: `- Number`/`- Name` pairs first
// (sorted by base, Number before Name), then other fields in first-seen order, Cost last.
function buildLineColumns(rows: Record<string, any>[]): string[] {
  const bases: string[] = [];
  const baseSeen = new Set<string>();
  const baseHasNumber = new Set<string>();
  const baseHasName = new Set<string>();
  const others: string[] = [];
  const otherSeen = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (key.endsWith(" - Number") || key.endsWith(" - Name")) {
        const base = key.replace(/ - (Number|Name)$/, "");
        if (!baseSeen.has(base)) {
          baseSeen.add(base);
          bases.push(base);
        }
        if (key.endsWith(" - Number")) baseHasNumber.add(base);
        else baseHasName.add(base);
      } else if (!otherSeen.has(key)) {
        otherSeen.add(key);
        others.push(key);
      }
    }
  }

  bases.sort();
  const columns: string[] = [];
  for (const base of bases) {
    if (baseHasNumber.has(base)) columns.push(`${base} - Number`);
    if (baseHasName.has(base)) columns.push(`${base} - Name`);
  }
  for (const key of others) {
    if (key !== "Cost") columns.push(key);
  }
  if (otherSeen.has("Cost")) columns.push("Cost");
  return columns;
}

function csvEscapeField(field: any): string {
  // The migrated data source returns null/undefined for empty fields (e.g. an unset
  // mf3_code) where the legacy Excel-table source returned ""; render both as blank
  // so the CSV matches the previous output instead of the literal text "null".
  if (field === null || field === undefined) {
    return "";
  }

  if (typeof field === "string") {
    if (field.includes(",") || field.includes('"') || field.includes("\n")) {
      const escaped = field.replace(/"/g, '""');
      return escaped;
    }
    return field;
  }

  // For non-strings: return as string without quotes
  return field;
}
